import { AppSettings } from 'src/app/core/configs/app.settings';
import { HttpService } from '../service/http.service';
import { Entity } from './entity';
import { ServiceHelper } from '../../helper/services.helper';
import { AlertControllerMessage, EntityFactoryInterface } from './entity-factory-interface';
import { Utilities } from '../../helper/util';

export class EntityFactory implements EntityFactoryInterface {

    constructor(public httpService: HttpService) {
    }

    async fetchObj<T>(objType: new (init?: any) => T, controller?: string, endpoint?: string, responseType?: string, hideLoad?: boolean) {

        const name = this.getName(objType, true);
        const msg = (`Fetching ${Utilities.splitCamelCaseToString(name)}`).trim();
        let loading;
        if (hideLoad !== true) {
            loading = await ServiceHelper.displayLoad(`${msg}. Please wait..`);
        }

        try {
            const ep = this.getUrl(objType, controller, endpoint);
            const response = await this.httpService?.fetch(ep, responseType);
            //Log.debug('Base response: ', response);
            if (Array.isArray(response)) {
                return (response as []).map(m => new objType(m));
            } else {
                const val = new objType(response);
                return [{ val }];
            }
        } catch (error: any) {
            console.error(error);
            ServiceHelper.displayAlert(`Error fetching ${name}`, error);
            throw new Error(error);
        } finally {
            loading?.dismiss();
        }
    }

    //
    // This function gets some arbitrary response from server, shouldn't really be thought of 
    // as being used for entity retrieval at all. Controller is absolutely required, endpoint probably also
    // 
    async get(description: string, controller?: string, endpoint?: string, responseType?: string) {
        // if the description does not end with ing then add ing
        let action = description;
        if (!action.endsWith('ing')) {
            action += 'ing';
        }
        // uppercase the first letter
        action = action.charAt(0).toUpperCase() + action.slice(1);

        const loading = await ServiceHelper.displayLoad(`${action}. Please wait..`);

        try {
            const ep = `${AppSettings.apiURL}/${controller}/${endpoint}`;
            const response = await this.httpService?.fetch(ep, responseType);
            //Log.debug('Base response: ', response);
            return response;
        } catch (error: any) {
            console.error(error);
            ServiceHelper.displayAlert(`Error during ${description}`, error);
            throw new Error(error);
        } finally {
            loading?.dismiss();
        }
    }

    async delete(description: string, controller?: string, endpoint?: string, responseType?: string) {
        // if the description does not end with ing then add ing
        let action = description;
        if (!action.endsWith('ing')) {
            action += 'ing';
        }
        // uppercase the first letter
        action = action.charAt(0).toUpperCase() + action.slice(1);

        const loading = await ServiceHelper.displayLoad(`${action}. Please wait..`);

        try {
            const ep = `${AppSettings.apiURL}/${controller}/${endpoint}`;
            const response = await this.httpService?.delete(ep, responseType);
            //Log.debug('Base response: ', response);
            return response;
        } catch (error: any) {
            console.error(error);
            ServiceHelper.displayAlert(`Error during ${description}`, error);
            throw new Error(error);
        } finally {
            loading?.dismiss();
        }
    }

    // no automated message generation, just use passed in message and handle thrown error yourself
    async getRaw(description: string, controller?: string, endpoint?: string, responseType?: string, hideLoad?: boolean) {
        var loading;
        if (!hideLoad) {
            loading = await ServiceHelper.displayLoad(description);
        }
        try {
            const ep = `${AppSettings.apiURL}/${controller}/${endpoint}`;
            const response = await this.httpService?.fetch(ep, responseType);
            //Log.debug('Base response: ', response);
            return response;
        } catch (error: any) {
            console.error(error);
            throw new Error(error);
        } finally {
            loading?.dismiss();
        }
    }

    async post(description: string, entity?: Entity, controller?: string, endpoint?: string, responseType?: string, alwaysReturnObj: boolean = true, silent: boolean = false) {
        // if the description does not end with ing then add ing
        let action = description;
        const name = (entity != null) ? ` ${this.getName(entity, true)}` : '';
        if (!action.includes(' ') && !action.endsWith('ing')) {
            action += 'ing';
        }
        // uppercase the first letter
        action = action.charAt(0).toUpperCase() + action.slice(1);

        const loading = silent ? null :
            await ServiceHelper.displayLoad(`${action}${Utilities.splitCamelCaseToString(name)}. Please wait..`);

        try {
            const ep = this.getUrl(entity, controller, endpoint);
            const result = await this.httpService?.post(entity, ep, (responseType || 'json'));
            return (alwaysReturnObj) ? result || entity : result;
        } catch (error: any) {
            console.error(error);
            error = error || `Unknown error occurred while ${action.toLowerCase()}. Please try again!`;
            if (!silent) {
                ServiceHelper.displayAlert(`Error ${action}`, error);
            }
            throw new Error(error);
        } finally {
            loading?.dismiss();
        }
    }

    async put(description: string, entity?: Entity, controller?: string, endpoint?: string, responseType?: string, silent?: boolean) {
        // if the description does not end with ing then add ing
        let action = description;
        const name = (entity != null) ? ` ${this.getName(entity, true)}` : '';
        if (!action.endsWith('ing')) {
            action += 'ing';
        }
        // uppercase the first letter
        action = action.charAt(0).toUpperCase() + action.slice(1);

        const loading = silent ? null : await ServiceHelper.displayLoad(`${action}${Utilities.splitCamelCaseToString(name)}, Please wait..`);

        try {
            const ep = this.getUrl(entity, controller, endpoint);
            const result = await this.httpService?.put(entity, ep, (responseType || 'json'));
            return result || entity;
        } catch (error: any) {
            console.error(error);
            error = error || `Unknown error occurred while ${action.toLowerCase()}. Please try again!`;
            if (!silent) {
                ServiceHelper.displayAlert(`Error ${action}`, error);
            }
            throw new Error(error);
        } finally {
            loading?.dismiss();
        }
    }

    async getObj<T>(objType: new (init?: any) => T, controller?: string, endpoint?: string, responseType?: string, runSilent: boolean = false) {

        const name = this.getName(objType);

        let loading;
        if (runSilent === false) {
            loading = await ServiceHelper.displayLoad(`Loading ${Utilities.splitCamelCaseToString(name)}. Please wait..`);
        }

        try {
            const ep = this.getUrl(objType, controller, endpoint);
            const response = await this.httpService?.fetch(ep, responseType);
            return new objType(response);
        } catch (error: any) {
            console.error(error);
            if (!runSilent) {
                ServiceHelper.displayAlert(`Error loading ${name}`, error);
            }
            throw new Error(error);
        } finally {
            loading?.dismiss();
        }
    }

    async saveObj(entity: Entity, controller?: string, endpoint?: string, responseType?: string, hideLoad?: boolean) {

        const name = this.getName(entity);
        const loading = hideLoad ? null :
            await ServiceHelper.displayLoad(`Saving ${Utilities.splitCamelCaseToString(name)}. Please wait..`);

        try {
            const ep = this.getUrl(entity, controller, endpoint);
            const result = ((entity as Entity).isUpdate(entity)) ?
                await this.httpService?.put(entity, entity.getUpdateUrl(entity, ep), (responseType || 'text')) :
                await this.httpService?.post(entity, ep, (responseType || 'json'));
            return result || entity;
        } catch (error: any) {
            console.error(error);
            error = error || 'Unknown error occurred while saving. Please try again!';
            if (!hideLoad) {
                ServiceHelper.displayAlert(`Error Saving ${name}`, error);
            }
            throw new Error(error);
        } finally {
            loading?.dismiss();
        }
    }

    async deleteObj(entity: Entity, controller?: string, endpoint?: string, confirm?: boolean, message?: AlertControllerMessage): Promise<boolean> {

        const name = this.getName(entity);

        if (confirm && await ServiceHelper.confirmSomething(
            message?.title ?? 'Confirm Deletion',
            message?.body ?? `Are you sure you want to delete this ${Utilities.splitCamelCaseToString(name)}?`) === false) {
            return false;
        } else {
            const loading = await ServiceHelper.displayLoad(`Deleting ${name}. Please wait..`);
            try {
                const ep = this.getUrl(entity, controller, endpoint);
                // try to get  value of static 'idField'
                var id = (+(entity[entity.idField as keyof Entity] ?? 0) as number);
                if (id > 0 == false) {
                    id = entity[entity.constructor['idField']];
                }
                await this.httpService?.deleteItem(id, ep, `json`);
                return true;
            } catch (error: any) {
                console.error(error);
                ServiceHelper.displayAlert(`Error deleting ${Utilities.splitCamelCaseToString(name)}`, error);
                throw new Error(error);
            } finally {
                loading?.dismiss();
            }
        }
    }

    async uploadBase64Images(entity: Entity, base64Imgs: string[], controller: string, endpoint: string) {
        const url = this.getUrl(entity, controller, endpoint);
        return ServiceHelper.httpSvc.post({ base64Imgs }, url, 'text');
    }

    private getController<T>(objType: T, controller?: string) {
        if (String.isNullOrWhiteSpace(controller)) {
            return this.getName(objType, true).replace(/ /g, '').toLowerCase();
        }
        return controller.toLowerCase();
    }

    private getName(entity: any, pluralize = false) {

        if (String.isNullOrWhiteSpace(entity.className) &&
            String.isNullOrWhiteSpace(entity.constructor['className'])) {
            console.error('Entity does not have a className property!', entity);
            return 'Data';
        }

        // try reading as property, if not there, read as static variable
        var name = entity.constructor['friendlyName'] ??
            entity['friendlyName'] ??
            entity.constructor['className'] ??
            entity['className'];

        if (pluralize) {
            if (name.endsWith('y') && !name.endsWith('ey') && !name.endsWith('ay') && !name.endsWith('oy') && !name.endsWith('uy')) {
                name = name.substring(0, name.length - 1);
                name += 'ie';
            }
            name += 's';
        }
        return name;
    }

    private getClassName(entity: any, pluralize = false) {

        if (String.isNullOrWhiteSpace(entity.className) &&
            String.isNullOrWhiteSpace(entity.constructor['className'])) {
            console.error('Entity does not have a className property!', entity);
            return 'Data';
        }

        // try reading as property, if not there, read as static variable
        var name = entity.constructor['className'] ?? entity['className'];

        if (pluralize) {
            if (name.endsWith('y') && !name.endsWith('ey') && !name.endsWith('ay') && !name.endsWith('oy') && !name.endsWith('uy')) {
                name = name.substring(0, name.length - 1);
                name += 'ie';
            }
            name += 's';
        }
        return name;
    }

    private getUrl<T>(objType: T, controller?: string, endpoint?: string) {
        if (String.isNullOrWhiteSpace(controller)) {
            if (!String.isNullOrWhiteSpace(objType['controller'])) {
                controller = objType['controller'];
            } else {
                controller = this.getClassName(objType, true).replace(/ /g, '').toLowerCase();
            }
        }
        let ep = `${AppSettings.apiURL}/${controller}`;
        if (!String.isNullOrWhiteSpace(endpoint)) {
            ep += `/${endpoint}`;
        }
        return ep;
    }
}

// export interface AlertControllerMessage {
//     title: string;
//     body: string;
// }
